import {
	MemberBalanceGroup,
	MemberBreakdownItem,
} from '@/utils/balanceCalculations'
import { getShortName } from '@/utils/userNames'
import { GetUserType } from '@orbit/api-types'
import React from 'react'
import { Avatar } from '../Avatar'
import { formatAmount } from '../balancesFormat'
import {
	BreakdownContainer,
	BreakdownRow,
	BreakdownSentence,
	BreakdownSentenceAmount,
	ExpandChevron,
	MemberHeaderMain,
	MemberHeaderRow,
	MemberHeadline,
	MemberHeadlineAmount,
	MemberHeadlineLine,
	MemberHeadlineName,
	MemberSectionWrapper,
	RecordPayUpButton,
} from './MembersSection.styles'
import { TestIds } from '@/testIds'

interface Props {
	member: GetUserType
	group: MemberBalanceGroup | undefined
	isExpanded: boolean
	members: GetUserType[]
	showCurrencyCode: boolean
	onToggle: () => void
	onRecordPayupForBreakdown: (memberId: number, item: MemberBreakdownItem) => void
}

const renderBreakdownRow = (
	item: MemberBreakdownItem,
	subjectMember: GetUserType,
	memberById: (id: number) => GetUserType | undefined,
	showCurrencyCode: boolean,
	onRecord: () => void,
): React.ReactNode => {
	const subjectName = getShortName(subjectMember)
	const otherName = getShortName(memberById(item.otherUserId))

	const subjectReceives = item.amount > 0
	const tone: 'owe' | 'owed' = subjectReceives ? 'owed' : 'owe'

	const amountStr = formatAmount(item.amount, item.currencyCode)
	const code = showCurrencyCode ? ` ${item.currencyCode.toUpperCase()}` : ''

	const debtor = subjectReceives ? otherName : subjectName
	const creditor = subjectReceives ? subjectName : otherName

	return (
		<BreakdownRow key={`${item.otherUserId}-${item.currencyCode}-${item.amount}`}>
			<BreakdownSentence>
				<MemberHeadlineName>{debtor}</MemberHeadlineName> owes{' '}
				<BreakdownSentenceAmount $tone={tone}>
					{amountStr}
					{code}
				</BreakdownSentenceAmount>{' '}
				to <MemberHeadlineName>{creditor}</MemberHeadlineName>
			</BreakdownSentence>
			<RecordPayUpButton type="button" onClick={onRecord} data-testid={TestIds.payUp.balanceCell(otherName)}>
				Record Pay Up
			</RecordPayUpButton>
		</BreakdownRow>
	)
}

const MemberRow: React.FC<Props> = ({
	member,
	group,
	isExpanded,
	members,
	showCurrencyCode,
	onToggle,
	onRecordPayupForBreakdown,
}) => {
	const balances = group?.balancesPerCurrency ?? []
	const isSettled = balances.length === 0
	const memberName = getShortName(member)

	const gets = balances.filter(b => b.amount > 0)
	const owes = balances.filter(b => b.amount < 0)

	const amountText = (cents: number, currencyCode: string): string => {
		const code = showCurrencyCode ? ` ${currencyCode.toUpperCase()}` : ''
		return `${formatAmount(cents, currencyCode)}${code}`
	}

	const breakdown = group?.breakdown ?? []
	const sortedBreakdown = [...breakdown]
		.filter(b => b.amount !== 0)
		.sort(
			(a, b) =>
				a.otherUserId - b.otherUserId || a.currencyCode.localeCompare(b.currencyCode),
		)

	const memberById = (id: number): GetUserType | undefined => members.find(m => m.id === id)

	return (
		<MemberSectionWrapper>
			<MemberHeaderRow
				type="button"
				$clickable={!isSettled}
				onClick={() => !isSettled && onToggle()}
				aria-expanded={!isSettled ? isExpanded : undefined}
			>
				<Avatar member={member} />
				<MemberHeaderMain>
					<MemberHeadline>
						{isSettled && (
							<MemberHeadlineLine>
								<MemberHeadlineName>{memberName}</MemberHeadlineName> is even with everyone
							</MemberHeadlineLine>
						)}
						{gets.length > 0 && (
							<MemberHeadlineLine>
								<MemberHeadlineName>{memberName}</MemberHeadlineName> gets back{' '}
								{gets.map((b, i) => (
									<React.Fragment key={b.currencyCode}>
										{i > 0 && ' + '}
										<MemberHeadlineAmount $tone="owed">
											{amountText(b.amount, b.currencyCode)}
										</MemberHeadlineAmount>
									</React.Fragment>
								))}{' '}
								in total
							</MemberHeadlineLine>
						)}
						{owes.length > 0 && (
							<MemberHeadlineLine>
								<MemberHeadlineName>{memberName}</MemberHeadlineName> owes{' '}
								{owes.map((b, i) => (
									<React.Fragment key={b.currencyCode}>
										{i > 0 && ' + '}
										<MemberHeadlineAmount $tone="owe">
											{amountText(b.amount, b.currencyCode)}
										</MemberHeadlineAmount>
									</React.Fragment>
								))}{' '}
								in total
							</MemberHeadlineLine>
						)}
					</MemberHeadline>
				</MemberHeaderMain>
				{!isSettled && <ExpandChevron $expanded={isExpanded}>›</ExpandChevron>}
			</MemberHeaderRow>

			{!isSettled && isExpanded && (
				<BreakdownContainer>
					{sortedBreakdown.map(item =>
						renderBreakdownRow(item, member, memberById, showCurrencyCode, () =>
							onRecordPayupForBreakdown(member.id, item),
						),
					)}
				</BreakdownContainer>
			)}
		</MemberSectionWrapper>
	)
}

export default MemberRow
